import { type FC, type ReactNode, useState } from 'react';

import { useOrganizationAccessLevel } from '@crehana/compass.knowledgehub/hooks';
import { useTranslation } from '@crehana/i18n';
import Search from '@crehana/icons/Search';
import {
  colors,
  Dialog,
  DialogContent,
  type DialogProps,
  InverseButton,
  LazyImage,
  Loader,
  PrimaryButton,
  TextField,
  // biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
} from '@crehana/ui';
import { useUITheme } from '@crehana/web';
import InfiniteScroll from 'react-infinite-scroller';

import { AccessLevelEnum } from '@/shared/types/graphql/globals.b2b';
import { adminLocaleEs } from '@/views/AdminDashboard/i18n';

import useAdminDashboard from '../../hooks/useAdminDashboard';
import useSearchCourses from './hooks/useSearchCourses';

const { baseLighter } = colors;

interface AddCourseDialogProps extends DialogProps {
  title: string | ReactNode;
  onSave: (ids: number[]) => void;
  loading: boolean;
  excludeIds?: number[];
  multiple?: boolean;
}

const AddCourseDialog: FC<React.PropsWithChildren<AddCourseDialogProps>> = ({
  open,
  onDismiss,
  title,
  onSave,
  loading,
  excludeIds = [],
  multiple = false,
}) => {
  const { t } = useTranslation();
  const { isDark } = useUITheme();
  const { organizationId } = useAdminDashboard();
  const [selectedCourses, setSelectedCourses] = useState<number[]>([]);

  const { isPureLMSOrganization, loading: loadingPureLMSOrganization } =
    useOrganizationAccessLevel({
      orgId: organizationId,
    });

  const { courses, hasNextPage, customFetchMoreData, handleChange } =
    useSearchCourses({
      excludeIds,
      accessLevelEnum: isPureLMSOrganization
        ? AccessLevelEnum.CREHANA_ELEVATE
        : null,
      skip: loadingPureLMSOrganization,
    });

  return (
    <Dialog open={open} onDismiss={onDismiss} size="full" dark={isDark}>
      <DialogContent
        type="s"
        className="flex flex-col justify-center items-center"
      >
        <div className="mb-32">
          <h2 className="tw-font-pop font-normal text-40">{title}</h2>
        </div>
        <TextField
          hint={t('ADD_COURSE_DIALOG_SEARCH', {
            defaultValue: adminLocaleEs.es.ADD_COURSE_DIALOG_SEARCH,
          })}
          onChange={handleChange}
          startAdornment={<Search color={baseLighter} size={24} />}
          darkMode={isDark}
          full
        />
        <div
          style={{ maxHeight: '60vh' }}
          className="overflow-y-auto w-full mt-24 mb-32"
          id="team-course-search"
        >
          <InfiniteScroll
            loadMore={customFetchMoreData}
            hasMore={hasNextPage}
            loader={
              <div className="flex justify-center w-full py-16" key={0}>
                <Loader />
              </div>
            }
            useWindow={false}
            threshold={200}
            getScrollParent={() =>
              document.getElementById('team-course-search')
            }
          >
            {courses.map(course => (
              <div
                key={course.originalId}
                className={`p-24 flex items-center justify-between ${
                  isDark
                    ? 'hover:bg-gray-main-dark-mode'
                    : 'hover:bg-gray-lighter'
                }`}
              >
                <div className="flex items-center">
                  <LazyImage
                    src={course.promoImage || ''}
                    className="h-96 w-96 mr-16"
                    imgxParams={{ h: 96, w: 96, dpr: 2 }}
                    alt={course.title || ''}
                  />
                  <div>
                    <p className="font-subtitle3 mb-16">{course.title}</p>
                    <p className="font-body3">
                      Por: {course.professor?.fullName}
                    </p>
                  </div>
                </div>
                {course.originalId &&
                selectedCourses.includes(course.originalId) ? (
                  <InverseButton
                    label={t('ADD_COURSE_DIALOG_QUIT', {
                      defaultValue: adminLocaleEs.es.ADD_COURSE_DIALOG_QUIT,
                    })}
                    onClick={() => {
                      setSelectedCourses(
                        selectedCourses.filter(c => c !== course.originalId),
                      );
                    }}
                  />
                ) : (
                  <PrimaryButton
                    label={t('ADD_COURSE_DIALOG_ADD', {
                      defaultValue: adminLocaleEs.es.ADD_COURSE_DIALOG_ADD,
                    })}
                    onClick={() => {
                      if (course.originalId) {
                        let newCourses = [course.originalId];

                        if (multiple) {
                          newCourses = [...selectedCourses, course.originalId];
                        }

                        setSelectedCourses(newCourses);
                      }
                    }}
                  />
                )}
              </div>
            ))}
          </InfiniteScroll>
        </div>
        {selectedCourses.length > 0 && (
          <div className="flex justify-center">
            <PrimaryButton
              label={t('ADD_COURSE_DIALOG_SAVE', {
                defaultValue: adminLocaleEs.es.ADD_COURSE_DIALOG_SAVE,
              })}
              disabled={loading}
              onClick={() => onSave(selectedCourses)}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddCourseDialog;
